import apiClient from "../utils/apiClient";

/**
 * Affiliate Service - handles affiliate program API calls
 */
class AffiliateService {
  /**
   * Get current user's affiliate/application status
   * @returns {Promise<{isAffiliate: boolean, affiliateStatus?: string, referralCode?: string, applicationStatus?: string}>}
   */
  async getStatus() {
    const res = await apiClient.get("/api/casino/affiliates/status");
    return res.data;
  }

  /**
   * Submit an affiliate application
   * @param {object} applicationData - Application details
   * @param {string} applicationData.platform - Platform (youtube, twitch, etc)
   * @param {string} applicationData.channelUrl - URL to channel/profile
   * @param {number} applicationData.followerCount - Number of followers
   * @param {string} applicationData.promotionPlan - Description of promotion plan
   * @param {string} [applicationData.additionalNotes] - Optional notes
   * @returns {Promise<{success: boolean, applicationId: string, message: string}>}
   */
  async submitApplication(applicationData) {
    const res = await apiClient.post(
      "/api/casino/affiliates/apply",
      applicationData,
    );
    return res.data;
  }

  /**
   * Get affiliate dashboard data (for approved affiliates)
   * @returns {Promise<{affiliate, balance, stats, referredPlayers, recentEarnings}>}
   */
  async getDashboardData() {
    const res = await apiClient.get("/api/casino/affiliates/dashboard");
    return res.data;
  }

  /**
   * Request a payout
   * @param {object} payoutData - Payout request details
   * @param {number} payoutData.amount - Amount to withdraw
   * @param {string} payoutData.payoutMethod - 'paypal', 'bank_transfer', or 'crypto'
   * @param {object} payoutData.payoutDetails - Method-specific details
   * @returns {Promise<{success: boolean, payoutId: string, message: string}>}
   */
  async requestPayout(payoutData) {
    const res = await apiClient.post(
      "/api/casino/affiliates/payout",
      payoutData,
    );
    return res.data;
  }

  /**
   * Get payout history
   * @returns {Promise<{payouts: Array}>}
   */
  async getPayoutHistory() {
    const res = await apiClient.get("/api/casino/affiliates/payouts");
    return res.data;
  }

  /**
   * Get public affiliate program information (no auth required)
   * @returns {Promise<{cpaThreshold: number, minPayout: number, defaultCpaRate: number, defaultRevshareRate: number}>}
   */
  async getProgramInfo() {
    const res = await apiClient.get("/api/casino/affiliates/program-info");
    return res.data;
  }
}

const affiliateService = new AffiliateService();
export default affiliateService;
